(() => {
  const ROOT_SELECTOR = '[data-case-agree-root]';

  function normalizeText(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  function getScrollElement() {
    return document.scrollingElement || document.documentElement || document.body;
  }

  function getScrollPosition() {
    const el = getScrollElement();
    return {
      x: window.scrollX || window.pageXOffset || 0,
      y: el ? el.scrollTop : (window.scrollY || window.pageYOffset || 0),
    };
  }

  function restoreScrollPosition(pos, frames = 10) {
    let count = 0;

    function tick() {
      window.scrollTo(pos.x, pos.y);
      count += 1;
      if (count < frames) {
        requestAnimationFrame(tick);
      }
    }

    requestAnimationFrame(tick);
    setTimeout(() => window.scrollTo(pos.x, pos.y), 80);
    setTimeout(() => window.scrollTo(pos.x, pos.y), 180);
    setTimeout(() => window.scrollTo(pos.x, pos.y), 320);
  }

  function preserveScrollWhile(callback) {
    const pos = getScrollPosition();
    callback();
    restoreScrollPosition(pos, 12);
  }

  function getSection(root) {
    const sectionId = root.dataset.sectionId || '';
    return (
      document.getElementById(`ProductInfo-${sectionId}`) ||
      root.closest(`[id^="ProductInfo-"]`) ||
      root.closest('product-info') ||
      document
    );
  }

  function getOptionBlocks(section) {
    return Array.from(section.querySelectorAll('.product-form__input'));
  }

  function getBlockLabelText(block) {
    const el =
      block.querySelector('legend') ||
      block.querySelector('label.form__label') ||
      block.querySelector('.form__label');

    return normalizeText(el?.textContent || '');
  }

  function findOptionBlock(root) {
    const section = getSection(root);

    // 1순위: 리퀴드에서 직접 지정한 숨김 옵션 블록
    const directBlock = section.querySelector('[data-case-agree-option-block="true"]');
    if (directBlock) return directBlock;

    // 2순위: 기존 방식 fallback
    const optionName = normalizeText(root.dataset.optionName || '');
    const optionPosition = Number(root.dataset.optionPosition || 0);
    const blocks = getOptionBlocks(section);

    let block = blocks.find((item) => {
      const labelText = getBlockLabelText(item);
      return labelText === optionName || labelText.includes(optionName);
    });

    if (!block && optionPosition > 0 && blocks[optionPosition - 1]) {
      block = blocks[optionPosition - 1];
    }

    return block || null;
  }

  function hideOptionBlock(block) {
    if (!block) return;
    block.hidden = true;
    block.setAttribute('aria-hidden', 'true');
    block.style.setProperty('display', 'none', 'important');
  }

  function getSelectControl(block) {
    return block ? block.querySelector('select') : null;
  }

  function getRadioControls(block) {
    if (!block) return [];
    return Array.from(block.querySelectorAll('input[type="radio"]'));
  }

  function getCurrentOptionValue(root) {
    const block = findOptionBlock(root);
    if (!block) return '';

    hideOptionBlock(block);

    const select = getSelectControl(block);
    if (select) {
      return normalizeText(select.value);
    }

    const radios = getRadioControls(block);
    const checked = radios.find((radio) => radio.checked);
    return checked ? normalizeText(checked.value) : '';
  }

  function dispatchControlChange(control) {
    control.dispatchEvent(new Event('input', { bubbles: true }));
    control.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function setOptionValue(root, targetValue) {
    const target = normalizeText(targetValue);
    const block = findOptionBlock(root);
    if (!block || !target) return false;

    hideOptionBlock(block);

    const select = getSelectControl(block);
    if (select) {
      const matchedOption = Array.from(select.options).find(
        (opt) => normalizeText(opt.value) === target
      );

      if (!matchedOption) return false;

      if (normalizeText(select.value) !== target) {
        select.value = matchedOption.value;
        dispatchControlChange(select);
      }
      return true;
    }

    const radios = getRadioControls(block);
    const targetRadio = radios.find(
      (radio) => normalizeText(radio.value) === target
    );

    if (!targetRadio) return false;

    if (!targetRadio.checked) {
      targetRadio.checked = true;
      dispatchControlChange(targetRadio);
    }

    return true;
  }

  function renderInfoBox(root) {
    const checkbox = root.querySelector('[data-case-agree-checkbox]');
    const info = root.querySelector('[data-case-agree-info]');
    if (!checkbox || !info) return;

    info.hidden = !checkbox.checked;
  }

  function syncCheckboxFromOption(root) {
    const checkbox = root.querySelector('[data-case-agree-checkbox]');
    if (!checkbox) return;

    const agreeValue = normalizeText(root.dataset.agreeValue || '');
    const disagreeValue = normalizeText(root.dataset.disagreeValue || '');
    const currentValue = getCurrentOptionValue(root);

    if (currentValue === agreeValue) {
      checkbox.checked = true;
    } else if (currentValue === disagreeValue) {
      checkbox.checked = false;
    }

    renderInfoBox(root);
  }

  function syncOptionFromCheckbox(root) {
    const checkbox = root.querySelector('[data-case-agree-checkbox]');
    if (!checkbox) return;

    const agreeValue = root.dataset.agreeValue || '';
    const disagreeValue = root.dataset.disagreeValue || '';

    preserveScrollWhile(() => {
      setOptionValue(root, checkbox.checked ? agreeValue : disagreeValue);
      renderInfoBox(root);

      const blockNow = findOptionBlock(root);
      if (blockNow) hideOptionBlock(blockNow);
    });
  }

  function bindSectionWatcher(root) {
    const section = getSection(root);
    if (!section || section.dataset.caseAgreeWatcherBound === 'true') return;

    section.dataset.caseAgreeWatcherBound = 'true';

    section.addEventListener(
      'change',
      (event) => {
        const checkbox = root.querySelector('[data-case-agree-checkbox]');
        if (event.target === checkbox) return;

        const pos = getScrollPosition();

        const blockNow = findOptionBlock(root);
        if (blockNow) hideOptionBlock(blockNow);

        syncCheckboxFromOption(root);
        restoreScrollPosition(pos, 8);
      },
      true
    );

    const observer = new MutationObserver(() => {
      const pos = getScrollPosition();

      const blockNow = findOptionBlock(root);
      if (blockNow) hideOptionBlock(blockNow);

      restoreScrollPosition(pos, 6);
    });

    observer.observe(section, {
      childList: true,
      subtree: true,
    });
  }

  function initRoot(root) {
    if (!root || root.dataset.caseAgreeInitialized === 'true') return;
    root.dataset.caseAgreeInitialized = 'true';

    const checkbox = root.querySelector('[data-case-agree-checkbox]');
    if (!checkbox) return;

    const agreeValue = normalizeText(root.dataset.agreeValue || '');
    const disagreeValue = normalizeText(root.dataset.disagreeValue || '');
    if (!agreeValue || !disagreeValue) return;

    const block = findOptionBlock(root);
    if (block) hideOptionBlock(block);

    checkbox.addEventListener('change', () => {
      syncOptionFromCheckbox(root);
    });

    bindSectionWatcher(root);

    const currentValue = getCurrentOptionValue(root);

    if (currentValue === agreeValue) {
      checkbox.checked = true;
    } else {
      checkbox.checked = false;
    }

    renderInfoBox(root);
  }

  function initAll(scope = document) {
    const roots =
      scope.matches?.(ROOT_SELECTOR)
        ? [scope]
        : Array.from(scope.querySelectorAll?.(ROOT_SELECTOR) || []);

    roots.forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document));
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:select', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:reorder', () => {
    initAll(document);
  });

  const globalObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        initAll(node);
      });
    }
  });

  globalObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();